import { useState, useEffect, useCallback, useRef } from 'react';
import { useSession, useUser } from '@clerk/clerk-react';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';
import {
  AssetCategory,
  AssetType,
  AssetTypeFieldDefinition,
  AssetTypeSupplierMapping,
  Asset,
  FileRequirement,
  EntityMetadata,
  EvaluatedFileRequirement,
  CreateAssetCategoryDto,
  UpdateAssetCategoryDto,
  CreateFieldDefinitionDto,
  UpdateFieldDefinitionDto,
  CreateTypeFieldDefinitionDto,
  UpdateTypeFieldDefinitionDto,
  CreateAssetTypeDto,
  UpdateAssetTypeDto,
  CreateAssetDto,
  UpdateAssetDto,
  CreateFileRequirementDto,
  UpdateFileRequirementDto,
  CreateSupplierMappingDto,
  UpdateSupplierMappingDto,
  AssetFilters,
  FieldDefinition,
  AssetMaintenanceSchedule,
  CreateMaintenanceScheduleDto,
  UpdateMaintenanceScheduleDto,
  MaintenanceEvent,
  MaintenanceScheduleTemplate,
  CreateMaintenanceTemplateDto,
  UpdateMaintenanceTemplateDto,
  CreateAssetResponse,
  CompleteMaintenanceEventResponse,
  PaginatedAssetsResponse,
} from 'shared/types/asset';

// ============================================
// Asset Categories Hooks
// ============================================

interface UseAssetCategoriesReturn {
  categories: AssetCategory[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useAssetCategories = (): UseAssetCategoriesReturn => {
  const { session } = useSession();
  const [categories, setCategories] = useState<AssetCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCategories = useCallback(async () => {
    if (!session) return;

    try {
      setIsLoading(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/asset-categories`,
        { method: 'GET' },
        session
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch asset categories: ${response.status}`);
      }

      const data = await response.json();
      setCategories(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch asset categories';
      setError(errorMessage);
      console.error('Error fetching asset categories:', err);
    } finally {
      setIsLoading(false);
    }
  }, [session]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  return {
    categories,
    isLoading,
    error,
    refetch: fetchCategories,
  };
};

interface UseAssetCategoryReturn {
  category: AssetCategory | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useAssetCategory = (id: string): UseAssetCategoryReturn => {
  const { session } = useSession();
  const [category, setCategory] = useState<AssetCategory | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchCategory = useCallback(async () => {
    if (!session || !id) return;

    try {
      setIsLoading(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/asset-categories/${id}`,
        { method: 'GET' },
        session
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch asset category: ${response.status}`);
      }

      const data = await response.json();
      setCategory(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch asset category';
      setError(errorMessage);
      console.error('Error fetching asset category:', err);
    } finally {
      setIsLoading(false);
    }
  }, [session, id]);

  useEffect(() => {
    fetchCategory();
  }, [fetchCategory]);

  return {
    category,
    isLoading,
    error,
    refetch: fetchCategory,
  };
};

interface UseCreateAssetCategoryReturn {
  createCategory: (data: CreateAssetCategoryDto) => Promise<AssetCategory>;
  isCreating: boolean;
  error: string | null;
}

export const useCreateAssetCategory = (): UseCreateAssetCategoryReturn => {
  const { session } = useSession();
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createCategory = useCallback(async (data: CreateAssetCategoryDto): Promise<AssetCategory> => {
    if (!session) {
      throw new Error('No session available');
    }

    try {
      setIsCreating(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/asset-categories`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        },
        session
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to create asset category: ${response.status}`);
      }

      const category = await response.json();
      return category;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create asset category';
      setError(errorMessage);
      throw err;
    } finally {
      setIsCreating(false);
    }
  }, [session]);

  return {
    createCategory,
    isCreating,
    error,
  };
};

interface UseUpdateAssetCategoryReturn {
  updateCategory: (id: string, data: UpdateAssetCategoryDto) => Promise<AssetCategory>;
  isUpdating: boolean;
  error: string | null;
}

export const useUpdateAssetCategory = (): UseUpdateAssetCategoryReturn => {
  const { session } = useSession();
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateCategory = useCallback(async (id: string, data: UpdateAssetCategoryDto): Promise<AssetCategory> => {
    if (!session) {
      throw new Error('No session available');
    }

    try {
      setIsUpdating(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/asset-categories/${id}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        },
        session
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to update asset category: ${response.status}`);
      }

      const category = await response.json();
      return category;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update asset category';
      setError(errorMessage);
      throw err;
    } finally {
      setIsUpdating(false);
    }
  }, [session]);

  return {
    updateCategory,
    isUpdating,
    error,
  };
};

// ============================================
// Field Definitions Hooks
// ============================================

interface UseCreateFieldDefinitionReturn {
  createFieldDefinition: (data: CreateFieldDefinitionDto) => Promise<FieldDefinition>;
  isCreating: boolean;
  error: string | null;
}

export const useCreateFieldDefinition = (): UseCreateFieldDefinitionReturn => {
  const { session } = useSession();
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createFieldDefinition = useCallback(async (data: CreateFieldDefinitionDto): Promise<FieldDefinition> => {
    if (!session) {
      throw new Error('No session available');
    }

    if (!data.assetCategoryId) {
      throw new Error('Asset category ID is required');
    }

    try {
      setIsCreating(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/asset-categories/${data.assetCategoryId}/fields`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        },
        session
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to create field definition: ${response.status}`);
      }

      const fieldDefinition = await response.json();
      return fieldDefinition;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create field definition';
      setError(errorMessage);
      throw err;
    } finally {
      setIsCreating(false);
    }
  }, [session]);

  return {
    createFieldDefinition,
    isCreating,
    error,
  };
};

interface UseUpdateFieldDefinitionReturn {
  updateFieldDefinition: (categoryId: string, fieldId: string, data: UpdateFieldDefinitionDto) => Promise<FieldDefinition>;
  isUpdating: boolean;
  error: string | null;
}

export const useUpdateFieldDefinition = (): UseUpdateFieldDefinitionReturn => {
  const { session } = useSession();
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateFieldDefinition = useCallback(async (categoryId: string, fieldId: string, data: UpdateFieldDefinitionDto): Promise<FieldDefinition> => {
    if (!session) {
      throw new Error('No session available');
    }

    try {
      setIsUpdating(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/asset-categories/${categoryId}/fields/${fieldId}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        },
        session
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to update field definition: ${response.status}`);
      }

      const fieldDefinition = await response.json();
      return fieldDefinition;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update field definition';
      setError(errorMessage);
      throw err;
    } finally {
      setIsUpdating(false);
    }
  }, [session]);

  return {
    updateFieldDefinition,
    isUpdating,
    error,
  };
};

interface UseDeleteFieldDefinitionReturn {
  deleteFieldDefinition: (categoryId: string, fieldId: string) => Promise<void>;
  isDeleting: boolean;
  error: string | null;
}

export const useDeleteFieldDefinition = (): UseDeleteFieldDefinitionReturn => {
  const { session } = useSession();
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const deleteFieldDefinition = useCallback(async (categoryId: string, fieldId: string): Promise<void> => {
    if (!session) {
      throw new Error('No session available');
    }

    try {
      setIsDeleting(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/asset-categories/${categoryId}/fields/${fieldId}`,
        {
          method: 'DELETE',
        },
        session
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to delete field definition: ${response.status}`);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete field definition';
      setError(errorMessage);
      throw err;
    } finally {
      setIsDeleting(false);
    }
  }, [session]);

  return {
    deleteFieldDefinition,
    isDeleting,
    error,
  };
};

// ============================================
// Asset Type Hooks
// ============================================

interface UseAssetTypesReturn {
  types: AssetType[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useAssetTypes = (categoryId: string): UseAssetTypesReturn => {
  const { session } = useSession();
  const [types, setTypes] = useState<AssetType[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTypes = useCallback(async () => {
    if (!session || !categoryId) return;

    try {
      setIsLoading(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/asset-categories/${categoryId}/types`,
        { method: 'GET' },
        session
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch asset types: ${response.status}`);
      }

      const data = await response.json();
      setTypes(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch asset types';
      setError(errorMessage);
      console.error('Error fetching asset types:', err);
    } finally {
      setIsLoading(false);
    }
  }, [session, categoryId]);

  useEffect(() => {
    fetchTypes();
  }, [fetchTypes]);

  return { types, isLoading, error, refetch: fetchTypes };
};

interface UseCreateAssetTypeReturn {
  createAssetType: (data: CreateAssetTypeDto) => Promise<AssetType>;
  isCreating: boolean;
  error: string | null;
}

export const useCreateAssetType = (): UseCreateAssetTypeReturn => {
  const { session } = useSession();
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createAssetType = useCallback(async (data: CreateAssetTypeDto): Promise<AssetType> => {
    if (!session) {
      throw new Error('No session available');
    }

    if (!data.assetCategoryId) {
      throw new Error('Asset category ID is required');
    }

    try {
      setIsCreating(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/asset-categories/${data.assetCategoryId}/types`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        },
        session
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to create asset type: ${response.status}`);
      }

      return await response.json();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create asset type';
      setError(errorMessage);
      throw err;
    } finally {
      setIsCreating(false);
    }
  }, [session]);

  return { createAssetType, isCreating, error };
};

interface UseUpdateAssetTypeReturn {
  updateAssetType: (categoryId: string, typeId: string, data: UpdateAssetTypeDto) => Promise<AssetType>;
  isUpdating: boolean;
  error: string | null;
}

export const useUpdateAssetType = (): UseUpdateAssetTypeReturn => {
  const { session } = useSession();
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateAssetType = useCallback(async (categoryId: string, typeId: string, data: UpdateAssetTypeDto): Promise<AssetType> => {
    if (!session) {
      throw new Error('No session available');
    }

    try {
      setIsUpdating(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/asset-categories/${categoryId}/types/${typeId}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        },
        session
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to update asset type: ${response.status}`);
      }

      return await response.json();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update asset type';
      setError(errorMessage);
      throw err;
    } finally {
      setIsUpdating(false);
    }
  }, [session]);

  return { updateAssetType, isUpdating, error };
};

interface UseDeleteAssetTypeReturn {
  deleteAssetType: (categoryId: string, typeId: string) => Promise<void>;
  isDeleting: boolean;
  error: string | null;
}

export const useDeleteAssetType = (): UseDeleteAssetTypeReturn => {
  const { session } = useSession();
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const deleteAssetType = useCallback(async (categoryId: string, typeId: string): Promise<void> => {
    if (!session) {
      throw new Error('No session available');
    }

    try {
      setIsDeleting(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/asset-categories/${categoryId}/types/${typeId}`,
        {
          method: 'DELETE',
        },
        session
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to delete asset type: ${response.status}`);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete asset type';
      setError(errorMessage);
      throw err;
    } finally {
      setIsDeleting(false);
    }
  }, [session]);

  return {
    deleteAssetType,
    isDeleting,
    error,
  };
};

// ============================================
// Type-Level Field Definition Hooks
// ============================================

interface UseCreateTypeFieldDefinitionReturn {
  createTypeFieldDefinition: (categoryId: string, typeId: string, data: CreateTypeFieldDefinitionDto) => Promise<AssetTypeFieldDefinition>;
  isCreating: boolean;
  error: string | null;
}

export const useCreateTypeFieldDefinition = (): UseCreateTypeFieldDefinitionReturn => {
  const { session } = useSession();
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createTypeFieldDefinition = useCallback(async (
    categoryId: string,
    typeId: string,
    data: CreateTypeFieldDefinitionDto
  ): Promise<AssetTypeFieldDefinition> => {
    if (!session) {
      throw new Error('No session available');
    }

    try {
      setIsCreating(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/asset-categories/${categoryId}/types/${typeId}/fields`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        },
        session
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to create type field definition: ${response.status}`);
      }

      return await response.json();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create type field definition';
      setError(errorMessage);
      throw err;
    } finally {
      setIsCreating(false);
    }
  }, [session]);

  return { createTypeFieldDefinition, isCreating, error };
};

interface UseUpdateTypeFieldDefinitionReturn {
  updateTypeFieldDefinition: (categoryId: string, typeId: string, fieldId: string, data: UpdateTypeFieldDefinitionDto) => Promise<AssetTypeFieldDefinition>;
  isUpdating: boolean;
  error: string | null;
}

export const useUpdateTypeFieldDefinition = (): UseUpdateTypeFieldDefinitionReturn => {
  const { session } = useSession();
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateTypeFieldDefinition = useCallback(async (
    categoryId: string,
    typeId: string,
    fieldId: string,
    data: UpdateTypeFieldDefinitionDto
  ): Promise<AssetTypeFieldDefinition> => {
    if (!session) {
      throw new Error('No session available');
    }

    try {
      setIsUpdating(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/asset-categories/${categoryId}/types/${typeId}/fields/${fieldId}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        },
        session
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to update type field definition: ${response.status}`);
      }

      return await response.json();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update type field definition';
      setError(errorMessage);
      throw err;
    } finally {
      setIsUpdating(false);
    }
  }, [session]);

  return { updateTypeFieldDefinition, isUpdating, error };
};

interface UseDeleteTypeFieldDefinitionReturn {
  deleteTypeFieldDefinition: (categoryId: string, typeId: string, fieldId: string) => Promise<void>;
  isDeleting: boolean;
  error: string | null;
}

export const useDeleteTypeFieldDefinition = (): UseDeleteTypeFieldDefinitionReturn => {
  const { session } = useSession();
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const deleteTypeFieldDefinition = useCallback(async (
    categoryId: string,
    typeId: string,
    fieldId: string
  ): Promise<void> => {
    if (!session) {
      throw new Error('No session available');
    }

    try {
      setIsDeleting(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/asset-categories/${categoryId}/types/${typeId}/fields/${fieldId}`,
        {
          method: 'DELETE',
        },
        session
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to delete type field definition: ${response.status}`);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete type field definition';
      setError(errorMessage);
      throw err;
    } finally {
      setIsDeleting(false);
    }
  }, [session]);

  return { deleteTypeFieldDefinition, isDeleting, error };
};

// ============================================
// Assets Hooks
// ============================================

interface UseAssetsReturn {
  assets: Asset[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useAssets = (filters?: AssetFilters): UseAssetsReturn => {
  const { session } = useSession();
  const [assets, setAssets] = useState<Asset[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Stringify filters to avoid object reference issues
  const filtersKey = JSON.stringify(filters || {});

  const fetchAssets = useCallback(async () => {
    if (!session) return;

    // Determine if we should fetch or wait for filters
    // Case 1: No filters parameter passed (undefined) -> Fetch all (general page)
    // Case 2: Empty object {} -> Fetch all (general page)
    // Case 3: Object with keys but all values undefined/null -> Wait for props (entity-specific page)
    const filtersProvided = filters !== undefined;
    const hasFilterKeys = filters && Object.keys(filters).length > 0;
    const hasAnyFilterValue = filters && (
      filters.assetCategoryId ||
      filters.assetTypeId ||
      filters.buildingId ||
      filters.unitId ||
      filters.roomId !== undefined ||
      filters.status ||
      filters.search
    );

    // If filters object was explicitly passed with keys but no values, wait for props
    if (filtersProvided && hasFilterKeys && !hasAnyFilterValue) {
      setAssets([]);
      setIsLoading(false);
      return;
    }

    // Otherwise, fetch (either with filters or all assets for general view)

    try {
      setIsLoading(true);
      setError(null);

      const queryParams = new URLSearchParams();
      if (filters?.assetCategoryId) queryParams.append('assetCategoryId', filters.assetCategoryId);
      if (filters?.assetTypeId) queryParams.append('assetTypeId', filters.assetTypeId);
      if (filters?.buildingId) queryParams.append('buildingId', filters.buildingId);
      if (filters?.unitId) queryParams.append('unitId', filters.unitId);
      if (filters?.roomId) queryParams.append('roomId', filters.roomId.toString());
      if (filters?.status) queryParams.append('status', filters.status);
      if (filters?.search) queryParams.append('search', filters.search);

      const url = `${API_URL}/assets${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

      const response = await fetchWithAuth(url, { method: 'GET' }, session);

      if (!response.ok) {
        throw new Error(`Failed to fetch assets: ${response.status}`);
      }

      const data = await response.json();

      // Fetch file-log-derived values for assets with connected fields
      const enrichedAssets = await Promise.all(
        data.map(async (asset: Asset) => {
          try {
            // Fetch file-log values for this asset
            const fileLogValuesResponse = await fetchWithAuth(
              `${API_URL}/assets/${asset.id}/file-log-values`,
              { method: 'GET' },
              session
            );

            if (fileLogValuesResponse.ok) {
              const fileLogValues = await fileLogValuesResponse.json();

              // Merge file-log values into asset fieldValues
              if (fileLogValues && Object.keys(fileLogValues).length > 0) {
                return {
                  ...asset,
                  fieldValues: {
                    ...(asset.fieldValues || {}),
                    ...fileLogValues
                  }
                };
              }
            }
          } catch (fileLogError) {
            console.warn(`Failed to fetch file-log values for asset ${asset.id}:`, fileLogError);
            // Continue without file-log values
          }

          return asset;
        })
      );

      setAssets(enrichedAssets);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch assets';
      setError(errorMessage);
      console.error('Error fetching assets:', err);
    } finally {
      setIsLoading(false);
    }
  }, [session, filtersKey]);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  return {
    assets,
    isLoading,
    error,
    refetch: fetchAssets,
  };
};

// ============================================
// Paginated Assets Hook (for AssetInstancesTable)
// ============================================

interface PaginatedAssetsFilters {
  categoryId?: number;
  assetTypeId?: number;
  status?: string;
  search?: string;
  buildingId?: string;
  unitId?: string;
  roomId?: number;
  scope?: string;
}

interface UseAssetsPaginatedReturn {
  assets: Asset[];
  totalCount: number;
  hasMore: boolean;
  categoryCounts: { id: number; name: string; count: number }[];
  typeCounts: { id: number; name: string; icon?: string; count: number }[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useAssetsPaginated = (
  filters: PaginatedAssetsFilters,
  page: number,
  pageSize: number,
): UseAssetsPaginatedReturn => {
  const { session } = useSession();
  const [assets, setAssets] = useState<Asset[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [categoryCounts, setCategoryCounts] = useState<{ id: number; name: string; count: number }[]>([]);
  const [typeCounts, setTypeCounts] = useState<{ id: number; name: string; icon?: string; count: number }[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const filtersKey = JSON.stringify({ ...filters, page, pageSize });

  const fetchAssets = useCallback(async () => {
    if (!session) return;

    try {
      setIsLoading(true);
      setError(null);

      const queryParams = new URLSearchParams();
      queryParams.append('page', String(page));
      queryParams.append('pageSize', String(pageSize));
      if (filters.categoryId) queryParams.append('categoryId', String(filters.categoryId));
      if (filters.assetTypeId) queryParams.append('assetTypeId', String(filters.assetTypeId));
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.search) queryParams.append('search', filters.search);
      if (filters.buildingId) queryParams.append('buildingId', filters.buildingId);
      if (filters.unitId) queryParams.append('unitId', filters.unitId);
      if (filters.roomId) queryParams.append('roomId', String(filters.roomId));
      if (filters.scope) queryParams.append('scope', filters.scope);

      const url = `${API_URL}/assets/paginated?${queryParams.toString()}`;
      const response = await fetchWithAuth(url, { method: 'GET' }, session);

      if (!response.ok) {
        throw new Error(`Failed to fetch paginated assets: ${response.status}`);
      }

      const result: PaginatedAssetsResponse = await response.json();

      // Enrich assets with file-log-derived values (only for the current page)
      const enrichedAssets = await Promise.all(
        result.data.map(async (asset: Asset) => {
          try {
            const fileLogValuesResponse = await fetchWithAuth(
              `${API_URL}/assets/${asset.id}/file-log-values`,
              { method: 'GET' },
              session,
            );

            if (fileLogValuesResponse.ok) {
              const fileLogValues = await fileLogValuesResponse.json();
              if (fileLogValues && Object.keys(fileLogValues).length > 0) {
                return {
                  ...asset,
                  fieldValues: {
                    ...(asset.fieldValues || {}),
                    ...fileLogValues,
                  },
                };
              }
            }
          } catch (fileLogError) {
            console.warn(`Failed to fetch file-log values for asset ${asset.id}:`, fileLogError);
          }
          return asset;
        }),
      );

      setAssets(enrichedAssets);
      setTotalCount(result.totalCount);
      setHasMore(result.hasMore);
      setCategoryCounts(result.categoryCounts);
      setTypeCounts(result.typeCounts);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch paginated assets';
      setError(errorMessage);
      console.error('Error fetching paginated assets:', err);
    } finally {
      setIsLoading(false);
    }
  }, [session, filtersKey]);

  useEffect(() => {
    fetchAssets();
  }, [fetchAssets]);

  return {
    assets,
    totalCount,
    hasMore,
    categoryCounts,
    typeCounts,
    isLoading,
    error,
    refetch: fetchAssets,
  };
};

export const useAssetsByType = (categoryId: number | null, typeId: number | null): UseAssetsReturn => {
  const filters: AssetFilters | undefined = (categoryId && typeId) ? {
    assetCategoryId: String(categoryId),
    assetTypeId: String(typeId),
  } : (categoryId ? { assetCategoryId: String(categoryId) } : undefined);

  return useAssets(filters);
};

interface UseAssetReturn {
  asset: Asset | null;
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useAsset = (id: string): UseAssetReturn => {
  const { session } = useSession();
  const [asset, setAsset] = useState<Asset | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAsset = useCallback(async () => {
    if (!session || !id) return;

    try {
      setIsLoading(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/assets/${id}`,
        { method: 'GET' },
        session
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch asset: ${response.status}`);
      }

      const data = await response.json();
      setAsset(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch asset';
      setError(errorMessage);
      console.error('Error fetching asset:', err);
    } finally {
      setIsLoading(false);
    }
  }, [session, id]);

  useEffect(() => {
    fetchAsset();
  }, [fetchAsset]);

  return {
    asset,
    isLoading,
    error,
    refetch: fetchAsset,
  };
};

interface UseCreateAssetReturn {
  createAsset: (data: CreateAssetDto) => Promise<CreateAssetResponse>;
  isCreating: boolean;
  error: string | null;
}

export const useCreateAsset = (): UseCreateAssetReturn => {
  const { session } = useSession();
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createAsset = useCallback(async (data: CreateAssetDto): Promise<CreateAssetResponse> => {
    if (!session) {
      throw new Error('No session available');
    }

    try {
      setIsCreating(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/assets`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        },
        session
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to create asset: ${response.status}`);
      }

      const result: CreateAssetResponse = await response.json();
      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create asset';
      setError(errorMessage);
      throw err;
    } finally {
      setIsCreating(false);
    }
  }, [session]);

  return {
    createAsset,
    isCreating,
    error,
  };
};

interface UseUpdateAssetReturn {
  updateAsset: (id: string, data: UpdateAssetDto) => Promise<Asset>;
  isUpdating: boolean;
  error: string | null;
}

export const useUpdateAsset = (): UseUpdateAssetReturn => {
  const { session } = useSession();
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateAsset = useCallback(async (id: string, data: UpdateAssetDto): Promise<Asset> => {
    if (!session) {
      throw new Error('No session available');
    }

    try {
      setIsUpdating(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/assets/${id}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        },
        session
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to update asset: ${response.status}`);
      }

      const asset = await response.json();
      return asset;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update asset';
      setError(errorMessage);
      throw err;
    } finally {
      setIsUpdating(false);
    }
  }, [session]);

  return {
    updateAsset,
    isUpdating,
    error,
  };
};

interface UseDeleteAssetReturn {
  deleteAsset: (id: string) => Promise<void>;
  isDeleting: boolean;
  error: string | null;
}

export const useDeleteAsset = (): UseDeleteAssetReturn => {
  const { session } = useSession();
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const deleteAsset = useCallback(async (id: string): Promise<void> => {
    if (!session) {
      throw new Error('No session available');
    }

    try {
      setIsDeleting(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/assets/${id}`,
        {
          method: 'DELETE',
        },
        session
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to delete asset: ${response.status}`);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete asset';
      setError(errorMessage);
      throw err;
    } finally {
      setIsDeleting(false);
    }
  }, [session]);

  return {
    deleteAsset,
    isDeleting,
    error,
  };
};

// ============================================
// File Requirements Hooks (Type-Level)
// ============================================

interface UseAssetTypeFileRequirementsReturn {
  requirements: FileRequirement[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useAssetTypeFileRequirements = (typeId: string): UseAssetTypeFileRequirementsReturn => {
  const { session } = useSession();
  const [requirements, setRequirements] = useState<FileRequirement[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRequirements = useCallback(async () => {
    if (!session || !typeId) return;

    try {
      setIsLoading(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/file-requirements?assetTypeId=${typeId}`,
        { method: 'GET' },
        session
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch file requirements: ${response.status}`);
      }

      const data = await response.json();
      setRequirements(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch file requirements';
      setError(errorMessage);
      console.error('Error fetching file requirements:', err);
    } finally {
      setIsLoading(false);
    }
  }, [session, typeId]);

  useEffect(() => {
    fetchRequirements();
  }, [fetchRequirements]);

  return {
    requirements,
    isLoading,
    error,
    refetch: fetchRequirements,
  };
};

// Legacy alias for backwards compatibility - now uses typeId instead of categoryId
export const useAssetFileRequirements = useAssetTypeFileRequirements;

interface UseCreateFileRequirementReturn {
  createFileRequirement: (data: CreateFileRequirementDto) => Promise<FileRequirement>;
  isCreating: boolean;
  error: string | null;
}

export const useCreateFileRequirement = (): UseCreateFileRequirementReturn => {
  const { session } = useSession();
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createFileRequirement = useCallback(async (data: CreateFileRequirementDto): Promise<FileRequirement> => {
    if (!session) {
      throw new Error('No session available');
    }

    try {
      setIsCreating(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/file-requirements`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        },
        session
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to create file requirement: ${response.status}`);
      }

      const requirement = await response.json();
      return requirement;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create file requirement';
      setError(errorMessage);
      throw err;
    } finally {
      setIsCreating(false);
    }
  }, [session]);

  return {
    createFileRequirement,
    isCreating,
    error,
  };
};

interface UseUpdateFileRequirementReturn {
  updateFileRequirement: (id: string, data: UpdateFileRequirementDto) => Promise<FileRequirement>;
  isUpdating: boolean;
  error: string | null;
}

export const useUpdateFileRequirement = (): UseUpdateFileRequirementReturn => {
  const { session } = useSession();
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateFileRequirement = useCallback(async (id: string, data: UpdateFileRequirementDto): Promise<FileRequirement> => {
    if (!session) {
      throw new Error('No session available');
    }

    try {
      setIsUpdating(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/file-requirements/${id}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data),
        },
        session
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to update file requirement: ${response.status}`);
      }

      const requirement = await response.json();
      return requirement;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update file requirement';
      setError(errorMessage);
      throw err;
    } finally {
      setIsUpdating(false);
    }
  }, [session]);

  return {
    updateFileRequirement,
    isUpdating,
    error,
  };
};

// ============================================
// File Labels Hooks
// ============================================

export interface FileLabel {
  id: number;
  label: string;
  resourceType?: 'building' | 'unit' | null;
  documentType?: 'document' | 'picture' | null;
}

interface UseFileLabelsReturn {
  fileLabels: FileLabel[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useFileLabels = (): UseFileLabelsReturn => {
  const { session } = useSession();
  const [fileLabels, setFileLabels] = useState<FileLabel[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchFileLabels = useCallback(async () => {
    if (!session) return;

    try {
      setIsLoading(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/file/file-labels`,
        { method: 'GET' },
        session
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch file labels: ${response.status}`);
      }

      const data = await response.json();
      setFileLabels(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch file labels';
      setError(errorMessage);
      console.error('Error fetching file labels:', err);
    } finally {
      setIsLoading(false);
    }
  }, [session]);

  useEffect(() => {
    fetchFileLabels();
  }, [fetchFileLabels]);

  return {
    fileLabels,
    isLoading,
    error,
    refetch: fetchFileLabels,
  };
};

// ============================================
// Entity Metadata Hooks
// ============================================

interface UseEntityMetadataReturn {
  metadata: EntityMetadata | null;
  isLoading: boolean;
  error: string | null;
}

export const useEntityMetadata = (): UseEntityMetadataReturn => {
  const { session } = useSession();
  const [metadata, setMetadata] = useState<EntityMetadata | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchMetadata = async () => {
      if (!session) return;

      try {
        setIsLoading(true);
        setError(null);

        const response = await fetchWithAuth(
          `${API_URL}/entity-metadata`,
          { method: 'GET' },
          session
        );

        if (!response.ok) {
          throw new Error(`Failed to fetch entity metadata: ${response.status}`);
        }

        const data = await response.json();
        setMetadata(data);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to fetch entity metadata';
        setError(errorMessage);
        console.error('Error fetching entity metadata:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchMetadata();
  }, [session]);

  return {
    metadata,
    isLoading,
    error,
  };
};

interface UseEvaluateFileRequirementsReturn {
  evaluatedRequirements: EvaluatedFileRequirement[];
  isLoading: boolean;
  error: string | null;
}

export const useEvaluateFileRequirements = (assetId: string): UseEvaluateFileRequirementsReturn => {
  const { session } = useSession();
  const [evaluatedRequirements, setEvaluatedRequirements] = useState<EvaluatedFileRequirement[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchEvaluated = async () => {
      if (!session || !assetId) return;

      try {
        setIsLoading(true);
        setError(null);

        const response = await fetchWithAuth(
          `${API_URL}/assets/${assetId}/file-requirements`,
          { method: 'GET' },
          session
        );

        if (!response.ok) {
          throw new Error(`Failed to evaluate file requirements: ${response.status}`);
        }

        const data = await response.json();
        setEvaluatedRequirements(data);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to evaluate file requirements';
        setError(errorMessage);
        console.error('Error evaluating file requirements:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchEvaluated();
  }, [session, assetId]);

  return {
    evaluatedRequirements,
    isLoading,
    error,
  };
};

// ============================================
// Bulk Asset Creation Hook
// ============================================

interface UseCreateBulkAssetsReturn {
  createBulkAssets: (assets: CreateAssetDto[]) => Promise<Asset[]>;
  isCreating: boolean;
  error: string | null;
}

export const useCreateBulkAssets = (): UseCreateBulkAssetsReturn => {
  const { session } = useSession();
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createBulkAssets = useCallback(async (assets: CreateAssetDto[]): Promise<Asset[]> => {
    if (!session) {
      throw new Error('No session available');
    }

    try {
      setIsCreating(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/assets/bulk`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ assets }),
        },
        session
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to create assets: ${response.status}`);
      }

      return await response.json();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create assets';
      setError(errorMessage);
      throw err;
    } finally {
      setIsCreating(false);
    }
  }, [session]);

  return {
    createBulkAssets,
    isCreating,
    error,
  };
};

// ============================================
// Asset Maintenance Schedule Hooks
// ============================================

interface UseAssetMaintenanceSchedulesReturn {
  schedules: AssetMaintenanceSchedule[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useAssetMaintenanceSchedules = (assetId: string): UseAssetMaintenanceSchedulesReturn => {
  const { session } = useSession();
  const sessionRef = useRef(session);
  sessionRef.current = session;

  const [schedules, setSchedules] = useState<AssetMaintenanceSchedule[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasLoadedOnce = useRef(false);

  const fetchSchedules = useCallback(async () => {
    if (!sessionRef.current || !assetId) return;

    try {
      // Only show loading spinner on initial load, not on background refetches
      if (!hasLoadedOnce.current) {
        setIsLoading(true);
      }
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/assets/${assetId}/maintenance-schedules`,
        { method: 'GET' },
        sessionRef.current
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch maintenance schedules: ${response.status}`);
      }

      const data = await response.json();
      setSchedules(data);
      hasLoadedOnce.current = true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch maintenance schedules';
      setError(errorMessage);
      console.error('Error fetching maintenance schedules:', err);
    } finally {
      setIsLoading(false);
    }
  }, [assetId]);

  useEffect(() => {
    fetchSchedules();
  }, [fetchSchedules]);

  return {
    schedules,
    isLoading,
    error,
    refetch: fetchSchedules,
  };
};

// Fetch all maintenance schedules with optional date range (for calendar)
interface UseAllMaintenanceSchedulesReturn {
  schedules: AssetMaintenanceSchedule[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useAllMaintenanceSchedules = (startDate?: string, endDate?: string): UseAllMaintenanceSchedulesReturn => {
  const { session } = useSession();
  const [schedules, setSchedules] = useState<AssetMaintenanceSchedule[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSchedules = useCallback(async () => {
    if (!session) return;

    try {
      setIsLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const url = `${API_URL}/asset-maintenance-schedules/all${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await fetchWithAuth(url, { method: 'GET' }, session);

      if (!response.ok) {
        throw new Error(`Failed to fetch maintenance schedules: ${response.status}`);
      }

      const data = await response.json();
      setSchedules(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch maintenance schedules';
      setError(errorMessage);
      console.error('Error fetching all maintenance schedules:', err);
    } finally {
      setIsLoading(false);
    }
  }, [session, startDate, endDate]);

  useEffect(() => {
    fetchSchedules();
  }, [fetchSchedules]);

  return { schedules, isLoading, error, refetch: fetchSchedules };
};

// Fetch upcoming maintenance schedules within N days
interface UseUpcomingMaintenanceReturn {
  schedules: AssetMaintenanceSchedule[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useUpcomingMaintenance = (days: number = 30): UseUpcomingMaintenanceReturn => {
  const { session } = useSession();
  const [schedules, setSchedules] = useState<AssetMaintenanceSchedule[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSchedules = useCallback(async () => {
    if (!session) return;

    try {
      setIsLoading(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/asset-maintenance-schedules/upcoming?days=${days}`,
        { method: 'GET' },
        session
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch upcoming maintenance: ${response.status}`);
      }

      const data = await response.json();
      setSchedules(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch upcoming maintenance';
      setError(errorMessage);
      console.error('Error fetching upcoming maintenance:', err);
    } finally {
      setIsLoading(false);
    }
  }, [session, days]);

  useEffect(() => {
    fetchSchedules();
  }, [fetchSchedules]);

  return { schedules, isLoading, error, refetch: fetchSchedules };
};

// Fetch overdue maintenance schedules
interface UseOverdueMaintenanceReturn {
  schedules: AssetMaintenanceSchedule[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useOverdueMaintenance = (): UseOverdueMaintenanceReturn => {
  const { session } = useSession();
  const [schedules, setSchedules] = useState<AssetMaintenanceSchedule[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchSchedules = useCallback(async () => {
    if (!session) return;

    try {
      setIsLoading(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/asset-maintenance-schedules/overdue`,
        { method: 'GET' },
        session
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch overdue maintenance: ${response.status}`);
      }

      const data = await response.json();
      setSchedules(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch overdue maintenance';
      setError(errorMessage);
      console.error('Error fetching overdue maintenance:', err);
    } finally {
      setIsLoading(false);
    }
  }, [session]);

  useEffect(() => {
    fetchSchedules();
  }, [fetchSchedules]);

  return { schedules, isLoading, error, refetch: fetchSchedules };
};

interface UseCreateMaintenanceScheduleReturn {
  createSchedule: (assetId: string, dto: CreateMaintenanceScheduleDto) => Promise<AssetMaintenanceSchedule>;
  isCreating: boolean;
  error: string | null;
}

export const useCreateMaintenanceSchedule = (): UseCreateMaintenanceScheduleReturn => {
  const { session } = useSession();
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createSchedule = useCallback(async (assetId: string, dto: CreateMaintenanceScheduleDto): Promise<AssetMaintenanceSchedule> => {
    if (!session) {
      throw new Error('No session available');
    }

    try {
      setIsCreating(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/assets/${assetId}/maintenance-schedules`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(dto),
        },
        session
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to create maintenance schedule: ${response.status}`);
      }

      return await response.json();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create maintenance schedule';
      setError(errorMessage);
      throw err;
    } finally {
      setIsCreating(false);
    }
  }, [session]);

  return {
    createSchedule,
    isCreating,
    error,
  };
};

interface UseUpdateMaintenanceScheduleReturn {
  updateSchedule: (scheduleId: number, dto: UpdateMaintenanceScheduleDto) => Promise<AssetMaintenanceSchedule>;
  isUpdating: boolean;
  error: string | null;
}

export const useUpdateMaintenanceSchedule = (): UseUpdateMaintenanceScheduleReturn => {
  const { session } = useSession();
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateSchedule = useCallback(async (scheduleId: number, dto: UpdateMaintenanceScheduleDto): Promise<AssetMaintenanceSchedule> => {
    if (!session) {
      throw new Error('No session available');
    }

    try {
      setIsUpdating(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/asset-maintenance-schedules/${scheduleId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(dto),
        },
        session
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to update maintenance schedule: ${response.status}`);
      }

      return await response.json();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update maintenance schedule';
      setError(errorMessage);
      throw err;
    } finally {
      setIsUpdating(false);
    }
  }, [session]);

  return {
    updateSchedule,
    isUpdating,
    error,
  };
};

interface UseCompleteMaintenanceEventReturn {
  completeEvent: (scheduleId: number) => Promise<AssetMaintenanceSchedule>;
  isCompleting: boolean;
  error: string | null;
}

export const useCompleteMaintenanceEvent = (): UseCompleteMaintenanceEventReturn => {
  const { session } = useSession();
  const [isCompleting, setIsCompleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const completeEvent = useCallback(async (scheduleId: number): Promise<AssetMaintenanceSchedule> => {
    if (!session) {
      throw new Error('No session available');
    }

    try {
      setIsCompleting(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/asset-maintenance-schedules/${scheduleId}/complete`,
        {
          method: 'PUT',
        },
        session
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to complete maintenance event: ${response.status}`);
      }

      return await response.json();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to complete maintenance event';
      setError(errorMessage);
      throw err;
    } finally {
      setIsCompleting(false);
    }
  }, [session]);

  return {
    completeEvent,
    isCompleting,
    error,
  };
};

interface UseDeleteMaintenanceScheduleReturn {
  deleteSchedule: (scheduleId: number) => Promise<void>;
  isDeleting: boolean;
  error: string | null;
}

export const useDeleteMaintenanceSchedule = (): UseDeleteMaintenanceScheduleReturn => {
  const { session } = useSession();
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const deleteSchedule = useCallback(async (scheduleId: number): Promise<void> => {
    if (!session) {
      throw new Error('No session available');
    }

    try {
      setIsDeleting(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/asset-maintenance-schedules/${scheduleId}`,
        {
          method: 'DELETE',
        },
        session
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to delete maintenance schedule: ${response.status}`);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete maintenance schedule';
      setError(errorMessage);
      throw err;
    } finally {
      setIsDeleting(false);
    }
  }, [session]);

  return {
    deleteSchedule,
    isDeleting,
    error,
  };
};

// ============================================
// Maintenance Event Hooks
// ============================================

interface UseMaintenanceEventsReturn {
  events: MaintenanceEvent[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useMaintenanceEvents = (startDate?: string, endDate?: string): UseMaintenanceEventsReturn => {
  const { session } = useSession();
  const sessionRef = useRef(session);
  sessionRef.current = session;

  const [events, setEvents] = useState<MaintenanceEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasLoadedOnce = useRef(false);

  const fetchEvents = useCallback(async () => {
    if (!sessionRef.current) return;

    try {
      if (!hasLoadedOnce.current) {
        setIsLoading(true);
      }
      setError(null);

      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);

      const url = `${API_URL}/maintenance-events${params.toString() ? `?${params.toString()}` : ''}`;
      const response = await fetchWithAuth(url, { method: 'GET' }, sessionRef.current);

      if (!response.ok) {
        throw new Error(`Failed to fetch maintenance events: ${response.status}`);
      }

      const data = await response.json();
      setEvents(data);
      hasLoadedOnce.current = true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch maintenance events';
      setError(errorMessage);
      console.error('Error fetching maintenance events:', err);
    } finally {
      setIsLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  return { events, isLoading, error, refetch: fetchEvents };
};

interface UseScheduleEventsReturn {
  events: MaintenanceEvent[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useScheduleEvents = (scheduleId: number): UseScheduleEventsReturn => {
  const { session } = useSession();
  const sessionRef = useRef(session);
  sessionRef.current = session;

  const [events, setEvents] = useState<MaintenanceEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const hasLoadedOnce = useRef(false);

  const fetchEvents = useCallback(async () => {
    if (!sessionRef.current || !scheduleId) return;

    try {
      if (!hasLoadedOnce.current) {
        setIsLoading(true);
      }
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/maintenance-events/schedule/${scheduleId}`,
        { method: 'GET' },
        sessionRef.current
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch schedule events: ${response.status}`);
      }

      const data = await response.json();
      setEvents(data);
      hasLoadedOnce.current = true;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch schedule events';
      setError(errorMessage);
      console.error('Error fetching schedule events:', err);
    } finally {
      setIsLoading(false);
    }
  }, [scheduleId]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  return { events, isLoading, error, refetch: fetchEvents };
};

interface UseCompleteMaintenanceEventWithDetailsReturn {
  completeEvent: (scheduleId: number, summary?: string, reportFile?: File) => Promise<CompleteMaintenanceEventResponse>;
  isCompleting: boolean;
  error: string | null;
}

export const useCompleteMaintenanceEventWithDetails = (): UseCompleteMaintenanceEventWithDetailsReturn => {
  const { session } = useSession();
  const { user } = useUser();
  const sessionRef = useRef(session);
  sessionRef.current = session;

  const [isCompleting, setIsCompleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const completeEvent = useCallback(async (
    scheduleId: number,
    summary?: string,
    reportFile?: File,
  ): Promise<CompleteMaintenanceEventResponse> => {
    if (!sessionRef.current) {
      throw new Error('No session available');
    }

    try {
      setIsCompleting(true);
      setError(null);

      const formData = new FormData();
      if (summary) formData.append('summary', summary);
      if (reportFile) formData.append('reportFile', reportFile);

      const response = await fetchWithAuth(
        `${API_URL}/asset-maintenance-schedules/${scheduleId}/complete`,
        {
          method: 'PUT',
          body: formData,
          headers: user?.id ? { 'x-user-id': user.id } : undefined,
        },
        sessionRef.current
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to complete maintenance event: ${response.status}`);
      }

      return await response.json();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to complete maintenance event';
      setError(errorMessage);
      throw err;
    } finally {
      setIsCompleting(false);
    }
  }, []);

  return {
    completeEvent,
    isCompleting,
    error,
  };
};

// ============================================
// Update Next Due Date Hook
// ============================================

interface UseUpdateNextDueDateReturn {
  updateNextDueDate: (scheduleId: number, nextDueDate: string) => Promise<AssetMaintenanceSchedule>;
  isUpdating: boolean;
  error: string | null;
}

export const useUpdateNextDueDate = (): UseUpdateNextDueDateReturn => {
  const { session } = useSession();
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateNextDueDate = useCallback(async (
    scheduleId: number,
    nextDueDate: string,
  ): Promise<AssetMaintenanceSchedule> => {
    if (!session) {
      throw new Error('No session available');
    }

    try {
      setIsUpdating(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/asset-maintenance-schedules/${scheduleId}/next-due-date`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ nextDueDate }),
        },
        session
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to update next due date: ${response.status}`);
      }

      return await response.json();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update next due date';
      setError(errorMessage);
      throw err;
    } finally {
      setIsUpdating(false);
    }
  }, [session]);

  return { updateNextDueDate, isUpdating, error };
};

// ============================================
// Create Ad-Hoc Event Hook
// ============================================

interface UseCreateAdhocEventReturn {
  createAdhocEvent: (scheduleId: number, dueDate: string, notes?: string) => Promise<MaintenanceEvent>;
  isCreating: boolean;
  error: string | null;
}

export const useCreateAdhocEvent = (): UseCreateAdhocEventReturn => {
  const { session } = useSession();
  const sessionRef = useRef(session);
  sessionRef.current = session;

  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createAdhocEvent = useCallback(async (
    scheduleId: number,
    dueDate: string,
    notes?: string,
  ): Promise<MaintenanceEvent> => {
    if (!sessionRef.current) {
      throw new Error('No session available');
    }

    try {
      setIsCreating(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/asset-maintenance-schedules/${scheduleId}/adhoc-event`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ dueDate, notes }),
        },
        sessionRef.current
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to create ad-hoc event: ${response.status}`);
      }

      return await response.json();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create ad-hoc event';
      setError(errorMessage);
      throw err;
    } finally {
      setIsCreating(false);
    }
  }, []);

  return { createAdhocEvent, isCreating, error };
};

// ============================================
// Maintenance Schedule Template Hooks
// ============================================

interface UseMaintenanceTemplatesReturn {
  templates: MaintenanceScheduleTemplate[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useMaintenanceTemplatesForType = (assetTypeId: number | null): UseMaintenanceTemplatesReturn => {
  const { session } = useSession();
  const [templates, setTemplates] = useState<MaintenanceScheduleTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchTemplates = useCallback(async () => {
    if (!session || !assetTypeId) return;

    try {
      setIsLoading(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/asset-types/${assetTypeId}/maintenance-templates`,
        { method: 'GET' },
        session
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch maintenance templates: ${response.status}`);
      }

      const data = await response.json();
      setTemplates(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch maintenance templates';
      setError(errorMessage);
      console.error('Error fetching maintenance templates:', err);
    } finally {
      setIsLoading(false);
    }
  }, [session, assetTypeId]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  return { templates, isLoading, error, refetch: fetchTemplates };
};

interface UseCreateMaintenanceTemplateReturn {
  createTemplate: (assetTypeId: number, dto: CreateMaintenanceTemplateDto) => Promise<MaintenanceScheduleTemplate>;
  isCreating: boolean;
  error: string | null;
}

export const useCreateMaintenanceTemplate = (): UseCreateMaintenanceTemplateReturn => {
  const { session } = useSession();
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createTemplate = useCallback(async (
    assetTypeId: number,
    dto: CreateMaintenanceTemplateDto
  ): Promise<MaintenanceScheduleTemplate> => {
    if (!session) {
      throw new Error('No session available');
    }

    try {
      setIsCreating(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/asset-types/${assetTypeId}/maintenance-templates`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(dto),
        },
        session
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to create maintenance template: ${response.status}`);
      }

      return await response.json();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create maintenance template';
      setError(errorMessage);
      throw err;
    } finally {
      setIsCreating(false);
    }
  }, [session]);

  return { createTemplate, isCreating, error };
};

interface UseUpdateMaintenanceTemplateReturn {
  updateTemplate: (templateId: number, dto: UpdateMaintenanceTemplateDto) => Promise<MaintenanceScheduleTemplate>;
  isUpdating: boolean;
  error: string | null;
}

export const useUpdateMaintenanceTemplate = (): UseUpdateMaintenanceTemplateReturn => {
  const { session } = useSession();
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateTemplate = useCallback(async (
    templateId: number,
    dto: UpdateMaintenanceTemplateDto
  ): Promise<MaintenanceScheduleTemplate> => {
    if (!session) {
      throw new Error('No session available');
    }

    try {
      setIsUpdating(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/maintenance-templates/${templateId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(dto),
        },
        session
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to update maintenance template: ${response.status}`);
      }

      return await response.json();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update maintenance template';
      setError(errorMessage);
      throw err;
    } finally {
      setIsUpdating(false);
    }
  }, [session]);

  return { updateTemplate, isUpdating, error };
};

interface UseDeleteMaintenanceTemplateReturn {
  deleteTemplate: (templateId: number) => Promise<void>;
  isDeleting: boolean;
  error: string | null;
}

export const useDeleteMaintenanceTemplate = (): UseDeleteMaintenanceTemplateReturn => {
  const { session } = useSession();
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const deleteTemplate = useCallback(async (templateId: number): Promise<void> => {
    if (!session) {
      throw new Error('No session available');
    }

    try {
      setIsDeleting(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/maintenance-templates/${templateId}`,
        {
          method: 'DELETE',
        },
        session
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to delete maintenance template: ${response.status}`);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete maintenance template';
      setError(errorMessage);
      throw err;
    } finally {
      setIsDeleting(false);
    }
  }, [session]);

  return { deleteTemplate, isDeleting, error };
};

// ============================================
// Resolve Maintenance Template Conflicts
// ============================================

interface UseResolveTemplateConflictsReturn {
  resolveConflicts: (assetId: string, templateIds: number[]) => Promise<AssetMaintenanceSchedule[]>;
  isResolving: boolean;
  error: string | null;
}

export const useResolveTemplateConflicts = (): UseResolveTemplateConflictsReturn => {
  const { session } = useSession();
  const [isResolving, setIsResolving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resolveConflicts = useCallback(async (
    assetId: string,
    templateIds: number[],
  ): Promise<AssetMaintenanceSchedule[]> => {
    if (!session) {
      throw new Error('No session available');
    }

    try {
      setIsResolving(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/assets/${assetId}/resolve-maintenance-templates`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ templateIds }),
        },
        session
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to resolve template conflicts: ${response.status}`);
      }

      return await response.json();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to resolve template conflicts';
      setError(errorMessage);
      throw err;
    } finally {
      setIsResolving(false);
    }
  }, [session]);

  return { resolveConflicts, isResolving, error };
};

// ============================================
// Supplier Mapping Hooks
// ============================================

interface UseSupplierMappingsForTypeReturn {
  mappings: AssetTypeSupplierMapping[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useSupplierMappingsForType = (assetTypeId: number | null): UseSupplierMappingsForTypeReturn => {
  const { session } = useSession();
  const [mappings, setMappings] = useState<AssetTypeSupplierMapping[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchMappings = useCallback(async () => {
    if (!session || !assetTypeId) return;

    try {
      setIsLoading(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/asset-types/${assetTypeId}/supplier-mappings`,
        { method: 'GET' },
        session
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch supplier mappings: ${response.status}`);
      }

      const data = await response.json();
      setMappings(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch supplier mappings';
      setError(errorMessage);
      console.error('Error fetching supplier mappings:', err);
    } finally {
      setIsLoading(false);
    }
  }, [session, assetTypeId]);

  useEffect(() => {
    fetchMappings();
  }, [fetchMappings]);

  return { mappings, isLoading, error, refetch: fetchMappings };
};

interface UseCreateSupplierMappingReturn {
  createMapping: (assetTypeId: number, dto: CreateSupplierMappingDto) => Promise<AssetTypeSupplierMapping>;
  isCreating: boolean;
  error: string | null;
}

export const useCreateSupplierMapping = (): UseCreateSupplierMappingReturn => {
  const { session } = useSession();
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createMapping = useCallback(async (
    assetTypeId: number,
    dto: CreateSupplierMappingDto
  ): Promise<AssetTypeSupplierMapping> => {
    if (!session) {
      throw new Error('No session available');
    }

    try {
      setIsCreating(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/asset-types/${assetTypeId}/supplier-mappings`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(dto),
        },
        session
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to create supplier mapping: ${response.status}`);
      }

      return await response.json();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create supplier mapping';
      setError(errorMessage);
      throw err;
    } finally {
      setIsCreating(false);
    }
  }, [session]);

  return { createMapping, isCreating, error };
};

interface UseUpdateSupplierMappingReturn {
  updateMapping: (mappingId: number, dto: UpdateSupplierMappingDto) => Promise<AssetTypeSupplierMapping>;
  isUpdating: boolean;
  error: string | null;
}

export const useUpdateSupplierMapping = (): UseUpdateSupplierMappingReturn => {
  const { session } = useSession();
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const updateMapping = useCallback(async (
    mappingId: number,
    dto: UpdateSupplierMappingDto
  ): Promise<AssetTypeSupplierMapping> => {
    if (!session) {
      throw new Error('No session available');
    }

    try {
      setIsUpdating(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/supplier-mappings/${mappingId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(dto),
        },
        session
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to update supplier mapping: ${response.status}`);
      }

      return await response.json();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to update supplier mapping';
      setError(errorMessage);
      throw err;
    } finally {
      setIsUpdating(false);
    }
  }, [session]);

  return { updateMapping, isUpdating, error };
};

interface UseDeleteSupplierMappingReturn {
  deleteMapping: (mappingId: number) => Promise<void>;
  isDeleting: boolean;
  error: string | null;
}

export const useDeleteSupplierMapping = (): UseDeleteSupplierMappingReturn => {
  const { session } = useSession();
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const deleteMapping = useCallback(async (mappingId: number): Promise<void> => {
    if (!session) {
      throw new Error('No session available');
    }

    try {
      setIsDeleting(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/supplier-mappings/${mappingId}`,
        {
          method: 'DELETE',
        },
        session
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to delete supplier mapping: ${response.status}`);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete supplier mapping';
      setError(errorMessage);
      throw err;
    } finally {
      setIsDeleting(false);
    }
  }, [session]);

  return { deleteMapping, isDeleting, error };
};

// ============================================
// Bulk Apply Maintenance Template Hooks
// ============================================

interface UseAffectedAssetsCountForTemplateReturn {
  fetchCount: (templateId: number) => Promise<{ toCreate: number; toRemove: number }>;
  isFetching: boolean;
  error: string | null;
}

export const useAffectedAssetsCountForTemplate = (): UseAffectedAssetsCountForTemplateReturn => {
  const { session } = useSession();
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCount = useCallback(async (templateId: number): Promise<{ toCreate: number; toRemove: number }> => {
    if (!session) {
      throw new Error('No session available');
    }

    try {
      setIsFetching(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/maintenance-templates/${templateId}/affected-assets-count`,
        { method: 'GET' },
        session
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to fetch affected assets count: ${response.status}`);
      }

      return await response.json();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch affected assets count';
      setError(errorMessage);
      throw err;
    } finally {
      setIsFetching(false);
    }
  }, [session]);

  return { fetchCount, isFetching, error };
};

interface UseBulkApplyTemplateReturn {
  bulkApply: (templateId: number, nextDueDate: string) => Promise<{ created: number; removed: number }>;
  isApplying: boolean;
  error: string | null;
}

export const useBulkApplyTemplate = (): UseBulkApplyTemplateReturn => {
  const { session } = useSession();
  const [isApplying, setIsApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const bulkApply = useCallback(async (templateId: number, nextDueDate: string): Promise<{ created: number; removed: number }> => {
    if (!session) {
      throw new Error('No session available');
    }

    try {
      setIsApplying(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/maintenance-templates/${templateId}/bulk-apply`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ nextDueDate }),
        },
        session
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to bulk apply template: ${response.status}`);
      }

      return await response.json();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to bulk apply template';
      setError(errorMessage);
      throw err;
    } finally {
      setIsApplying(false);
    }
  }, [session]);

  return { bulkApply, isApplying, error };
};

interface UseBulkApplySupplierMappingReturn {
  bulkApply: (mappingId: number) => Promise<{ updated: number }>;
  isApplying: boolean;
  error: string | null;
}

export const useBulkApplySupplierMapping = (): UseBulkApplySupplierMappingReturn => {
  const { session } = useSession();
  const [isApplying, setIsApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const bulkApply = useCallback(async (mappingId: number): Promise<{ updated: number }> => {
    if (!session) {
      throw new Error('No session available');
    }

    try {
      setIsApplying(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/supplier-mappings/${mappingId}/bulk-apply`,
        {
          method: 'POST',
        },
        session
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to bulk apply supplier mapping: ${response.status}`);
      }

      return await response.json();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to bulk apply supplier mapping';
      setError(errorMessage);
      throw err;
    } finally {
      setIsApplying(false);
    }
  }, [session]);

  return { bulkApply, isApplying, error };
};

interface UseAffectedScheduleCountReturn {
  fetchCount: (mappingId: number) => Promise<{ count: number }>;
  isFetching: boolean;
  error: string | null;
}

export const useAffectedScheduleCount = (): UseAffectedScheduleCountReturn => {
  const { session } = useSession();
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCount = useCallback(async (mappingId: number): Promise<{ count: number }> => {
    if (!session) {
      throw new Error('No session available');
    }

    try {
      setIsFetching(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/supplier-mappings/${mappingId}/affected-count`,
        { method: 'GET' },
        session
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to fetch affected count: ${response.status}`);
      }

      return await response.json();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch affected count';
      setError(errorMessage);
      throw err;
    } finally {
      setIsFetching(false);
    }
  }, [session]);

  return { fetchCount, isFetching, error };
};

// ============================================
// Propagate Template Changes
// ============================================

interface UseExistingSchedulesCountReturn {
  fetchCount: (templateId: number, previousName?: string) => Promise<{ count: number }>;
  isFetching: boolean;
  error: string | null;
}

export const useExistingSchedulesCount = (): UseExistingSchedulesCountReturn => {
  const { session } = useSession();
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchCount = useCallback(async (templateId: number, previousName?: string): Promise<{ count: number }> => {
    if (!session) {
      throw new Error('No session available');
    }

    try {
      setIsFetching(true);
      setError(null);

      const params = previousName ? `?previousName=${encodeURIComponent(previousName)}` : '';
      const response = await fetchWithAuth(
        `${API_URL}/maintenance-templates/${templateId}/existing-schedules-count${params}`,
        { method: 'GET' },
        session
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to fetch count: ${response.status}`);
      }

      return await response.json();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to fetch existing schedules count';
      setError(errorMessage);
      throw err;
    } finally {
      setIsFetching(false);
    }
  }, [session]);

  return { fetchCount, isFetching, error };
};

interface UsePropagateTemplateChangesReturn {
  propagate: (templateId: number, fields: Record<string, any>) => Promise<{ updated: number }>;
  isPropagating: boolean;
  error: string | null;
}

export const usePropagateTemplateChanges = (): UsePropagateTemplateChangesReturn => {
  const { session } = useSession();
  const [isPropagating, setIsPropagating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const propagate = useCallback(async (templateId: number, fields: Record<string, any>): Promise<{ updated: number }> => {
    if (!session) {
      throw new Error('No session available');
    }

    try {
      setIsPropagating(true);
      setError(null);

      const response = await fetchWithAuth(
        `${API_URL}/maintenance-templates/${templateId}/propagate`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(fields),
        },
        session
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Failed to propagate changes: ${response.status}`);
      }

      return await response.json();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to propagate template changes';
      setError(errorMessage);
      throw err;
    } finally {
      setIsPropagating(false);
    }
  }, [session]);

  return { propagate, isPropagating, error };
};

// ============================================
// Suggested Files Hook (unlinked files on same building/unit matching required labels)
// ============================================

interface SuggestedFile {
  id: number;
  label: string[];
  secondaryLabels: string[] | null;
  documentDate: string | null;
  expirationDate: string | null;
  status: string | null;
  sharePointFileUrl: string | null;
  sharePointFileId: string | null;
  buildingId: string | null;
  unitId: string | null;
  createdAt: string;
  summary: string | null;
  matchingLabels: string[];
  requirementType: string | null;
  fileLabelName: string | null;
}

interface UseSuggestedFilesReturn {
  suggestedFiles: SuggestedFile[];
  isLoading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export const useSuggestedFiles = (assetId: string | undefined): UseSuggestedFilesReturn => {
  const { session } = useSession();
  const [suggestedFiles, setSuggestedFiles] = useState<SuggestedFile[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSuggested = useCallback(async () => {
    if (!session || !assetId) return;
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/assets/${assetId}/suggested-files`,
        { method: 'GET' },
        session,
      );
      if (!response.ok) throw new Error('Fout bij ophalen voorgestelde bestanden');
      const data = await response.json();
      setSuggestedFiles(data);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [session, assetId]);

  useEffect(() => {
    fetchSuggested();
  }, [fetchSuggested]);

  return { suggestedFiles, isLoading, error, refetch: fetchSuggested };
};
